// ─── src/admin/HeroControlPage.jsx ───────────────────────────────────────────
//
// Full control panel for the Hero section — mobile responsive.
// Persists to Firestore collection "heroConfig", document "main".
//
// Tabs: Text · Video · Blobs · Animation
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Button, Slider, InputNumber, Switch,
  message, Typography, Space, Spin, Tag, Grid,
} from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// ── Firestore REST ────────────────────────────────────────────────────────────
const PROJECT_ID = 'portfolio-e79b8-5854c';
const API_KEY    = 'AIzaSyDLsmnqt6wth0b3hZYrnllXnJIcjUaK8Uc';
const REST_BASE  = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`;
const DOC_PATH   = `${REST_BASE}/heroConfig/main?key=${API_KEY}`;

function toValue(v) {
  if (v === null || v === undefined) return { nullValue: null };
  if (typeof v === 'boolean')        return { booleanValue: v };
  if (typeof v === 'number')         return Number.isInteger(v) ? { integerValue: String(v) } : { doubleValue: v };
  if (typeof v === 'string')         return { stringValue: v };
  if (Array.isArray(v))              return { arrayValue: { values: v.map(toValue) } };
  if (typeof v === 'object')         return { mapValue: { fields: toFields(v) } };
  return { stringValue: String(v) };
}
function toFields(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) out[k] = toValue(v);
  return out;
}
function fromValue(v) {
  if (!v) return null;
  if ('stringValue'  in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue'  in v) return Number(v.doubleValue);
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue'    in v) return null;
  if ('arrayValue'   in v) return (v.arrayValue.values || []).map(fromValue);
  if ('mapValue'     in v) return fromFields(v.mapValue.fields || {});
  return null;
}
function fromFields(fields) {
  const obj = {};
  for (const [k, v] of Object.entries(fields)) obj[k] = fromValue(v);
  return obj;
}

async function loadConfig() {
  const res = await fetch(DOC_PATH);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Firestore GET: ${res.status}`);
  const json = await res.json();
  return fromFields(json.fields || {});
}

async function saveConfig(data) {
  const fieldPaths = Object.keys(data)
    .map(k => `updateMask.fieldPaths=${encodeURIComponent(k)}`).join('&');
  const url = `${REST_BASE}/heroConfig/main?${fieldPaths}&key=${API_KEY}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields: toFields(data) }),
  });
  if (!res.ok) throw new Error(`Firestore PATCH: ${res.status}`);
  return res.json();
}

// ── Defaults ──────────────────────────────────────────────────────────────────
const DEFAULTS = {
  headlineText:            'Naveen Ak Fullstack Developer',
  scrollIndicatorText:     'SCROLL DOWN',
  footerText:              'To Explore',
  cursorText:              'scroll down ↓',

  headlineFontSize_desktop: 10.5,
  headlineFontSize_mobile:  11,
  headlineColor:            '#f0ece4',
  headlineLetterSpacing:    -3,
  headlineLineHeight:       105,

  videoDesktopUrl:          './des.mp4',
  videoMobileUrl:           './vid_m.mp4',
  videoObjectPosition:      'center center',

  revealScrub:              1.4,
  revealPinLength:          200,
  revealScaleStart:         0,
  revealScaleEnd:           1,
  revealBorderRadiusStart:  50,
  revealBorderRadiusEnd:    0,
  revealDuration:           0.7,
  revealEase:               'power2.inOut',

  scrollIndDelay:           0.5,
  scrollIndDuration:        0.8,

  blob1ColorStart:          'rgba(200,240,74,0.07)',
  blob1Top:                 5,
  blob1Right:               10,
  blob1Size_desktop:        500,
  blob1Size_mobile:         260,
  blob1Blur:                60,

  blob2ColorStart:          'rgba(0,229,255,0.04)',
  blob2Bottom:              10,
  blob2Left:                5,
  blob2Size_desktop:        350,
  blob2Size_mobile:         200,
  blob2Blur:                80,

  gridEnabled:              true,
  gridOpacity:              3,
  gridSize:                 55,
  gridColor:                '#c8f04a',

  blobPulseEnabled:         true,
  blobPulseMax:             3,
  blobPulseStrength:        0.06,
  blobPulseDuration:        0.5,
};

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function Section({ label, icon, color = '#c8f04a', children }) {
  return (
    <div style={{
      background: '#111', border: '1px solid #222',
      borderRadius: 12, padding: '16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <Text style={{
          fontFamily: 'Space Mono, monospace', fontSize: 11,
          letterSpacing: '0.22em', textTransform: 'uppercase', color,
        }}>
          {label}
        </Text>
      </div>
      {children}
    </div>
  );
}

// ── SliderField: stacks on mobile ─────────────────────────────────────────────
function SliderField({ label, hint, value, onChange, min, max, step = 1, unit = '', isMobile }) {
  if (isMobile) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <Text style={{ fontSize: 11, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <InputNumber
              size="small" min={min} max={max} step={step} value={value}
              onChange={v => v !== null && onChange(v)}
              style={{
                width: 64, background: '#0a0a0a', border: '1px solid #333',
                color: '#c8f04a', fontFamily: 'Space Mono, monospace', fontSize: 11,
              }}
            />
            {unit && (
              <Text style={{ fontSize: 10, color: '#555', fontFamily: 'Space Mono, monospace' }}>{unit}</Text>
            )}
          </div>
        </div>
        {hint && (
          <Text style={{ fontSize: 10, color: '#3a3a3a', display: 'block', marginBottom: 3 }}>{hint}</Text>
        )}
        <Slider
          min={min} max={max} step={step} value={value} onChange={onChange}
          styles={{ track: { background: '#c8f04a' }, handle: { borderColor: '#c8f04a' } }}
        />
      </div>
    );
  }

  // Desktop: label | slider | input
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '180px 1fr auto',
      alignItems: 'center',
      gap: 14, marginBottom: 14, minHeight: 36,
    }}>
      <div>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
        {hint && <div><Text style={{ fontSize: 10, color: '#3a3a3a' }}>{hint}</Text></div>}
      </div>
      <Slider
        min={min} max={max} step={step} value={value} onChange={onChange}
        styles={{ track: { background: '#c8f04a' }, handle: { borderColor: '#c8f04a' } }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 88 }}>
        <InputNumber
          size="small" min={min} max={max} step={step} value={value}
          onChange={v => v !== null && onChange(v)}
          style={{
            width: 70, background: '#0a0a0a', border: '1px solid #333',
            color: '#c8f04a', fontFamily: 'Space Mono, monospace', fontSize: 12,
          }}
        />
        {unit && (
          <Text style={{ fontSize: 10, color: '#555', fontFamily: 'Space Mono, monospace' }}>{unit}</Text>
        )}
      </div>
    </div>
  );
}

// ── SwitchField ───────────────────────────────────────────────────────────────
function SwitchField({ label, hint, value, onChange, isMobile }) {
  if (isMobile) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace', display: 'block' }}>{label}</Text>
          {hint && <Text style={{ fontSize: 10, color: '#3a3a3a', display: 'block' }}>{hint}</Text>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <Switch checked={value} onChange={onChange} style={{ background: value ? '#c8f04a' : '#333' }} />
          <Text style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: value ? '#c8f04a' : '#444' }}>
            {value ? 'On' : 'Off'}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '180px 1fr',
      alignItems: 'center', gap: 14, marginBottom: 14, minHeight: 36,
    }}>
      <div>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
        {hint && <div><Text style={{ fontSize: 10, color: '#3a3a3a' }}>{hint}</Text></div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Switch checked={value} onChange={onChange} style={{ background: value ? '#c8f04a' : '#333' }} />
        <Text style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: value ? '#c8f04a' : '#444' }}>
          {value ? 'Enabled' : 'Disabled'}
        </Text>
      </div>
    </div>
  );
}

// ── ColorField ────────────────────────────────────────────────────────────────
function ColorField({ label, hint, value, onChange, isMobile }) {
  const inner = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
      <input
        type="color"
        value={value.startsWith('#') ? value : '#000000'}
        onChange={e => onChange(e.target.value)}
        style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 6, padding: 2, flexShrink: 0 }}
      />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          flex: 1, background: '#0a0a0a', border: '1px solid #2a2a2a',
          borderRadius: 5, color: '#f0ece4',
          fontFamily: 'Space Mono, monospace', fontSize: 11,
          padding: '6px 9px', outline: 'none', minWidth: 0,
        }}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace', display: 'block', marginBottom: 6 }}>{label}</Text>
        {hint && <Text style={{ fontSize: 10, color: '#3a3a3a', display: 'block', marginBottom: 4 }}>{hint}</Text>}
        {inner}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 14, marginBottom: 14, minHeight: 36 }}>
      <div>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
        {hint && <div><Text style={{ fontSize: 10, color: '#3a3a3a' }}>{hint}</Text></div>}
      </div>
      {inner}
    </div>
  );
}

// ── TextareaField ─────────────────────────────────────────────────────────────
function TextareaField({ label, hint, value, onChange, rows = 2, mono = true }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
        {hint && <span style={{ fontSize: 10, color: '#3a3a3a', marginLeft: 8 }}>{hint}</span>}
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: '#0a0a0a', border: '1px solid #2a2a2a',
          borderRadius: 7, color: '#f0ece4', resize: 'vertical',
          fontFamily: mono ? 'Space Mono, monospace' : 'Syne, sans-serif',
          fontSize: mono ? 12 : 15,
          fontWeight: mono ? 400 : 700,
          padding: '9px 12px', outline: 'none', lineHeight: 1.6,
        }}
      />
    </div>
  );
}

// ── UrlField ──────────────────────────────────────────────────────────────────
function UrlField({ label, hint, value, onChange, placeholder = '' }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
        {hint && <span style={{ fontSize: 10, color: '#3a3a3a', marginLeft: 8 }}>{hint}</span>}
      </div>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: '#0a0a0a', border: '1px solid #2a2a2a',
          borderRadius: 6, color: '#f0ece4',
          fontFamily: 'Space Mono, monospace', fontSize: 11,
          padding: '8px 11px', outline: 'none',
        }}
      />
    </div>
  );
}

// ── RawColorInput (for rgba strings) ─────────────────────────────────────────
function RawColorInput({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace', display: 'block', marginBottom: 6 }}>
        {label}
      </Text>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="rgba(200,240,74,0.07)"
        style={{
          width: '100%', boxSizing: 'border-box',
          background: '#0a0a0a', border: '1px solid #2a2a2a',
          borderRadius: 6, color: '#f0ece4',
          fontFamily: 'Space Mono, monospace', fontSize: 11,
          padding: '8px 11px', outline: 'none',
        }}
      />
      <Text style={{ fontSize: 10, color: '#3a3a3a', display: 'block', marginTop: 4 }}>
        Format: rgba(R,G,B,opacity) — opacity controls glow intensity
      </Text>
    </div>
  );
}

// ── EaseField ─────────────────────────────────────────────────────────────────
function EaseField({ label, hint, value, onChange, isMobile }) {
  const eases = [
    'power1.inOut', 'power2.inOut', 'power3.inOut', 'power4.inOut',
    'power1.out', 'power2.out', 'power3.out', 'power4.out',
    'power1.in', 'power2.in',
    'expo.out', 'expo.inOut',
    'back.out(1.7)', 'back.inOut(1.7)',
    'elastic.out(1,0.3)',
    'sine.inOut', 'sine.out',
    'none',
  ];

  if (isMobile) {
    return (
      <div style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace', display: 'block', marginBottom: 4 }}>{label}</Text>
        {hint && <Text style={{ fontSize: 10, color: '#3a3a3a', display: 'block', marginBottom: 6 }}>{hint}</Text>}
        <select
          value={value} onChange={e => onChange(e.target.value)}
          style={{
            width: '100%', background: '#0a0a0a', border: '1px solid #2a2a2a',
            borderRadius: 6, color: '#f0ece4',
            fontFamily: 'Space Mono, monospace', fontSize: 11,
            padding: '8px 10px', outline: 'none',
          }}
        >
          {eases.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 14, marginBottom: 14, minHeight: 36 }}>
      <div>
        <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>{label}</Text>
        {hint && <div><Text style={{ fontSize: 10, color: '#3a3a3a' }}>{hint}</Text></div>}
      </div>
      <select
        value={value} onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', background: '#0a0a0a', border: '1px solid #2a2a2a',
          borderRadius: 6, color: '#f0ece4',
          fontFamily: 'Space Mono, monospace', fontSize: 11,
          padding: '7px 10px', outline: 'none',
        }}
      >
        {eases.map(e => <option key={e} value={e}>{e}</option>)}
      </select>
    </div>
  );
}

// ── TabBar: scrollable on mobile ──────────────────────────────────────────────
const TABS = ['Text', 'Video', 'Blobs', 'Animation'];
const TAB_COLORS = { Text: '#c8f04a', Video: '#00e5ff', Blobs: '#a78bfa', Animation: '#ff6b6b' };

function TabBar({ active, onChange, isMobile }) {
  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        overflowX: 'auto',
        gap: 4,
        marginBottom: 16,
        background: '#111',
        padding: 4,
        borderRadius: 10,
        border: '1px solid #222',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        {TABS.map(t => (
          <button key={t} onClick={() => onChange(t)} style={{
            flexShrink: 0,
            padding: '8px 16px',
            borderRadius: 8, border: 'none',
            cursor: 'pointer',
            fontFamily: 'Space Mono, monospace', fontSize: 10,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            background: active === t ? TAB_COLORS[t] : 'transparent',
            color: active === t ? '#000' : '#555',
            transition: 'all 0.18s ease',
            fontWeight: active === t ? 700 : 400,
            whiteSpace: 'nowrap',
          }}>
            {t}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', gap: 4, marginBottom: 20,
      background: '#111', padding: 4, borderRadius: 10, border: '1px solid #222',
    }}>
      {TABS.map(t => (
        <button key={t} onClick={() => onChange(t)} style={{
          flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
          cursor: 'pointer',
          fontFamily: 'Space Mono, monospace', fontSize: 11,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          background: active === t ? TAB_COLORS[t] : 'transparent',
          color: active === t ? '#000' : '#555',
          transition: 'all 0.18s ease',
          fontWeight: active === t ? 700 : 400,
        }}>
          {t}
        </button>
      ))}
    </div>
  );
}

// ── Hero mini-preview: fluid width ───────────────────────────────────────────
function HeroPreview({ cfg }) {
  const [vidErr, setVidErr] = React.useState(false);

  return (
    <div style={{
      background: '#000', border: '1px solid #222', borderRadius: 10,
      overflow: 'hidden', marginBottom: 16,
    }}>
      <div style={{
        padding: '6px 12px', borderBottom: '1px solid #1a1a1a',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4,
      }}>
        <Text style={{ fontFamily: 'Space Mono, monospace', fontSize: 9, color: '#555', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          Hero preview
        </Text>
        <Text style={{ fontFamily: 'Space Mono, monospace', fontSize: 9, color: '#333' }}>
          scroll-reveal shown open
        </Text>
      </div>

      <div style={{ position: 'relative', height: 180, overflow: 'hidden', background: '#000' }}>
        {/* Grid */}
        {cfg.gridEnabled && (
          <div style={{
            position: 'absolute', inset: 0,
            opacity: (cfg.gridOpacity || 3) / 100,
            backgroundImage: `linear-gradient(${cfg.gridColor} 1px,transparent 1px),linear-gradient(90deg,${cfg.gridColor} 1px,transparent 1px)`,
            backgroundSize: `${cfg.gridSize}px ${cfg.gridSize}px`,
            pointerEvents: 'none',
          }} />
        )}

        {/* Blob 1 */}
        <div style={{
          position: 'absolute',
          top: `${cfg.blob1Top}%`, right: `${cfg.blob1Right}%`,
          width: 90, height: 90,
          background: `radial-gradient(circle,${cfg.blob1ColorStart} 0%,transparent 65%)`,
          borderRadius: '50%',
          filter: `blur(${Math.round(cfg.blob1Blur / 3)}px)`,
          pointerEvents: 'none',
        }} />

        {/* Blob 2 */}
        <div style={{
          position: 'absolute',
          bottom: `${cfg.blob2Bottom}%`, left: `${cfg.blob2Left}%`,
          width: 70, height: 70,
          background: `radial-gradient(circle,${cfg.blob2ColorStart} 0%,transparent 65%)`,
          borderRadius: '50%',
          filter: `blur(${Math.round(cfg.blob2Blur / 3)}px)`,
          pointerEvents: 'none',
        }} />

        {/* Headline */}
        <div style={{
          position: 'absolute', top: '50%', left: '3%',
          transform: 'translateY(-50%)',
          fontFamily: 'Syne, sans-serif', fontWeight: 800,
          fontSize: `${Math.round((cfg.headlineFontSize_desktop || 10.5) * 0.85)}px`,
          letterSpacing: `${(cfg.headlineLetterSpacing || -3) / 100}em`,
          lineHeight: (cfg.headlineLineHeight || 105) / 100,
          color: cfg.headlineColor || '#f0ece4',
          zIndex: 3, maxWidth: '68%', wordBreak: 'break-word',
        }}>
          {cfg.headlineText || 'Naveen Ak Fullstack Developer'}
        </div>

        {/* Video overlay */}
        {cfg.videoDesktopUrl && !vidErr && (
          <video
            src={cfg.videoDesktopUrl}
            muted playsInline
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: cfg.videoObjectPosition || 'center center',
              zIndex: 4, opacity: 0.12,
            }}
            onError={() => setVidErr(true)}
          />
        )}

        {/* Scroll indicator */}
        <div style={{
          position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
          fontFamily: 'Space Mono, monospace', fontSize: 7,
          color: '#444', letterSpacing: '0.15em', zIndex: 6,
        }}>
          {cfg.scrollIndicatorText || 'SCROLL DOWN'}
          <div style={{ width: 1, height: 16, background: 'linear-gradient(to bottom,transparent,#c8f04a)' }} />
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HeroControlPage() {
  const [cfg, setCfg] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState('Text');
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    (async () => {
      try {
        const remote = await loadConfig();
        if (remote) setCfg(prev => ({ ...prev, ...remote }));
      } catch {
        message.warning('Could not load hero config — using defaults');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = useCallback((key, val) => {
    setCfg(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveConfig(cfg);
      message.success('Hero config saved ✓');
      setDirty(false);
    } catch (e) {
      message.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCfg({ ...DEFAULTS });
    setDirty(true);
    message.info('Reset to defaults — press Save to persist');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  // Shorthand — forwards isMobile to every field that needs it
  const sf = (props) => <SliderField {...props} isMobile={isMobile} />;

  return (
    <div style={{
      maxWidth: 800,
      margin: '0 auto',
      padding: isMobile ? '12px 10px 80px' : '0 0 64px',
    }}>

      {/* ── Page header ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: 20, flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <Title level={isMobile ? 5 : 4} style={{ margin: 0, color: '#f0ece4' }}>
            Hero Section Controller
          </Title>
          <Text style={{ color: '#555', fontSize: 11, fontFamily: 'Space Mono, monospace' }}>
            heroConfig/main — text, video, blobs, animation
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {dirty && !isMobile && (
            <Tag color="warning" style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>
              Unsaved
            </Tag>
          )}
          <Button
            size={isMobile ? 'small' : 'middle'}
            icon={<ReloadOutlined />}
            onClick={handleReset}
            style={{ background: '#111', border: '1px solid #333', color: '#aaa' }}
          >
            {isMobile ? '' : 'Reset'}
          </Button>
          <Button
            type="primary"
            size={isMobile ? 'small' : 'middle'}
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!dirty}
            style={{
              background: dirty ? '#c8f04a' : '#1a1a1a',
              border: 'none', color: dirty ? '#000' : '#444', fontWeight: 700,
            }}
          >
            {isMobile ? 'Save' : 'Save to Firestore'}
          </Button>
        </div>
      </div>

      {/* ── Live preview ── */}
      <HeroPreview cfg={cfg} />

      {/* ── Tabs ── */}
      <TabBar active={tab} onChange={setTab} isMobile={isMobile} />

      {/* ════════════════════════════════════════
          TEXT TAB
      ════════════════════════════════════════ */}
      {tab === 'Text' && (
        <>
          <Section label="Headline" icon="Aa" color="#c8f04a">
            <TextareaField
              label="Headline text"
              hint="Shown large behind the video"
              value={cfg.headlineText}
              onChange={v => set('headlineText', v)}
              rows={2}
              mono={false}
            />

            <ColorField
              isMobile={isMobile}
              label="Headline color"
              value={cfg.headlineColor}
              onChange={v => set('headlineColor', v)}
            />

            <SliderField
              isMobile={isMobile}
              label="Font size (desktop)"
              hint="vw — used in clamp(3rem, Nvw, 10rem)"
              value={cfg.headlineFontSize_desktop}
              onChange={v => set('headlineFontSize_desktop', v)}
              min={4} max={18} step={0.5} unit="vw"
            />

            <SliderField
              isMobile={isMobile}
              label="Font size (mobile)"
              hint="vw — used in clamp(3.2rem, Nvw, 4rem)"
              value={cfg.headlineFontSize_mobile}
              onChange={v => set('headlineFontSize_mobile', v)}
              min={4} max={20} step={0.5} unit="vw"
            />

            <SliderField
              isMobile={isMobile}
              label="Letter spacing"
              hint="-3 = -0.03em, 5 = 0.05em"
              value={cfg.headlineLetterSpacing}
              onChange={v => set('headlineLetterSpacing', v)}
              min={-10} max={20} step={1} unit="×0.01em"
            />

            <SliderField
              isMobile={isMobile}
              label="Line height"
              hint="105 = 1.05"
              value={cfg.headlineLineHeight}
              onChange={v => set('headlineLineHeight', v)}
              min={80} max={150} step={1} unit="×0.01"
            />
          </Section>

          <Section label="Scroll indicator" icon="↓" color="#c8f04a">
            <TextareaField
              label="Indicator label"
              hint="Text above the pulse line"
              value={cfg.scrollIndicatorText}
              onChange={v => set('scrollIndicatorText', v)}
              rows={1}
            />

            <SliderField
              isMobile={isMobile}
              label="Fade-in delay"
              hint="seconds after page load"
              value={cfg.scrollIndDelay}
              onChange={v => set('scrollIndDelay', v)}
              min={0} max={4} step={0.1} unit="s"
            />

            <SliderField
              isMobile={isMobile}
              label="Fade-in duration"
              value={cfg.scrollIndDuration}
              onChange={v => set('scrollIndDuration', v)}
              min={0.1} max={3} step={0.1} unit="s"
            />
          </Section>

          <Section label="Footer & cursor" icon="⌨" color="#c8f04a">
            <TextareaField
              label="Footer text"
              hint="Small text at very bottom"
              value={cfg.footerText}
              onChange={v => set('footerText', v)}
              rows={1}
            />

            <TextareaField
              label="Cursor label"
              hint="data-cursor-text on the section"
              value={cfg.cursorText}
              onChange={v => set('cursorText', v)}
              rows={1}
            />
          </Section>
        </>
      )}

      {/* ════════════════════════════════════════
          VIDEO TAB
      ════════════════════════════════════════ */}
      {tab === 'Video' && (
        <>
          <Section label="Video sources" icon="▶" color="#00e5ff">
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 16, marginBottom: 8,
            }}>
              {/* Desktop video card */}
              <div style={{ background: '#0a0a0a', border: '1px solid #1a1a1a', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{
                    padding: '2px 8px', borderRadius: 4,
                    background: '#00e5ff18', border: '1px solid #00e5ff44',
                    fontFamily: 'Space Mono, monospace', fontSize: 10,
                    color: '#00e5ff', letterSpacing: '0.14em', textTransform: 'uppercase',
                  }}>Desktop</div>
                  <Text style={{ fontSize: 10, color: '#333', fontFamily: 'Space Mono, monospace' }}>≥ 768px</Text>
                </div>

                <div style={{
                  width: '100%', aspectRatio: '16/9',
                  background: '#111', borderRadius: 6, overflow: 'hidden',
                  marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {cfg.videoDesktopUrl ? (
                    <video
                      src={cfg.videoDesktopUrl} muted playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <Text style={{ color: '#2a2a2a', fontSize: 11, fontFamily: 'Space Mono, monospace' }}>No video</Text>
                  )}
                </div>

                <UrlField
                  label="URL / path"
                  value={cfg.videoDesktopUrl}
                  onChange={v => set('videoDesktopUrl', v)}
                  placeholder="./des.mp4 or https://..."
                />
              </div>

              {/* Mobile video card */}
              <div style={{ background: '#0a0a0a', border: '1px solid #1a1a1a', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{
                    padding: '2px 8px', borderRadius: 4,
                    background: '#a78bfa18', border: '1px solid #a78bfa44',
                    fontFamily: 'Space Mono, monospace', fontSize: 10,
                    color: '#a78bfa', letterSpacing: '0.14em', textTransform: 'uppercase',
                  }}>Mobile</div>
                  <Text style={{ fontSize: 10, color: '#333', fontFamily: 'Space Mono, monospace' }}>&lt; 768px</Text>
                </div>

                <div style={{
                  width: '100%', aspectRatio: '16/9',
                  background: '#111', borderRadius: 6, overflow: 'hidden',
                  marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {cfg.videoMobileUrl ? (
                    <video
                      src={cfg.videoMobileUrl} muted playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <Text style={{ color: '#2a2a2a', fontSize: 11, fontFamily: 'Space Mono, monospace' }}>No video</Text>
                  )}
                </div>

                <UrlField
                  label="URL / path"
                  value={cfg.videoMobileUrl}
                  onChange={v => set('videoMobileUrl', v)}
                  placeholder="./vid_m.mp4 or https://..."
                />
              </div>
            </div>
          </Section>

          <Section label="Video display" icon="⊡" color="#00e5ff">
            <Text style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace', display: 'block', marginBottom: 10 }}>
              Object position
            </Text>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(3,1fr)' : 'repeat(3,1fr)',
              gap: isMobile ? 5 : 6,
            }}>
              {[
                'top left', 'top center', 'top right',
                'center left', 'center center', 'center right',
                'bottom left', 'bottom center', 'bottom right',
              ].map(pos => (
                <button
                  key={pos}
                  onClick={() => set('videoObjectPosition', pos)}
                  style={{
                    padding: isMobile ? '8px 2px' : '7px 0',
                    borderRadius: 6,
                    border: cfg.videoObjectPosition === pos ? '1px solid #00e5ff88' : '1px solid #1e1e1e',
                    background: cfg.videoObjectPosition === pos ? '#00e5ff18' : '#0a0a0a',
                    color: cfg.videoObjectPosition === pos ? '#00e5ff' : '#444',
                    fontFamily: 'Space Mono, monospace',
                    fontSize: isMobile ? 9 : 10,
                    cursor: 'pointer', letterSpacing: '0.05em',
                    transition: 'all 0.15s',
                    lineHeight: 1.3,
                  }}
                >
                  {pos}
                </button>
              ))}
            </div>
          </Section>
        </>
      )}

      {/* ════════════════════════════════════════
          BLOBS TAB
      ════════════════════════════════════════ */}
      {tab === 'Blobs' && (
        <>
          {/* Grid background */}
          <Section label="Grid background" icon="⊞" color="#a78bfa">
            <SwitchField
              isMobile={isMobile}
              label="Show grid"
              hint="Subtle line grid behind everything"
              value={cfg.gridEnabled}
              onChange={v => set('gridEnabled', v)}
            />
            <SliderField
              isMobile={isMobile}
              label="Opacity"
              hint="3 = 0.03 (very subtle)"
              value={cfg.gridOpacity}
              onChange={v => set('gridOpacity', v)}
              min={1} max={20} step={1} unit="×0.01"
            />
            <SliderField
              isMobile={isMobile}
              label="Grid size"
              value={cfg.gridSize}
              onChange={v => set('gridSize', v)}
              min={20} max={120} step={5} unit="px"
            />
            <ColorField
              isMobile={isMobile}
              label="Grid color"
              value={cfg.gridColor}
              onChange={v => set('gridColor', v)}
            />
          </Section>

          {/* Blob 1 */}
          <Section label="Blob 1 — top right (acid green)" icon="○" color="#c8f04a">
            <RawColorInput
              label="Color (rgba string)"
              value={cfg.blob1ColorStart}
              onChange={v => set('blob1ColorStart', v)}
            />
            <SliderField isMobile={isMobile} label="Top"           value={cfg.blob1Top}          onChange={v => set('blob1Top', v)}          min={0}   max={100}          unit="%" />
            <SliderField isMobile={isMobile} label="Right"         value={cfg.blob1Right}        onChange={v => set('blob1Right', v)}        min={0}   max={100}          unit="%" />
            <SliderField isMobile={isMobile} label="Size (desktop)" value={cfg.blob1Size_desktop} onChange={v => set('blob1Size_desktop', v)} min={100} max={900} step={10} unit="px" />
            <SliderField isMobile={isMobile} label="Size (mobile)"  value={cfg.blob1Size_mobile}  onChange={v => set('blob1Size_mobile', v)}  min={50}  max={500} step={10} unit="px" />
            <SliderField isMobile={isMobile} label="Blur"          value={cfg.blob1Blur}         onChange={v => set('blob1Blur', v)}         min={10}  max={200} step={5}  unit="px" />
          </Section>

          {/* Blob 2 */}
          <Section label="Blob 2 — bottom left (cyan)" icon="○" color="#00e5ff">
            <RawColorInput
              label="Color (rgba string)"
              value={cfg.blob2ColorStart}
              onChange={v => set('blob2ColorStart', v)}
            />
            <SliderField isMobile={isMobile} label="Bottom"        value={cfg.blob2Bottom}       onChange={v => set('blob2Bottom', v)}       min={0}   max={100}          unit="%" />
            <SliderField isMobile={isMobile} label="Left"          value={cfg.blob2Left}         onChange={v => set('blob2Left', v)}         min={0}   max={100}          unit="%" />
            <SliderField isMobile={isMobile} label="Size (desktop)" value={cfg.blob2Size_desktop} onChange={v => set('blob2Size_desktop', v)} min={100} max={900} step={10} unit="px" />
            <SliderField isMobile={isMobile} label="Size (mobile)"  value={cfg.blob2Size_mobile}  onChange={v => set('blob2Size_mobile', v)}  min={50}  max={500} step={10} unit="px" />
            <SliderField isMobile={isMobile} label="Blur"          value={cfg.blob2Blur}         onChange={v => set('blob2Blur', v)}         min={10}  max={200} step={5}  unit="px" />
          </Section>

          {/* Blob scroll pulse */}
          <Section label="Blob scroll pulse" icon="↑↓" color="#a78bfa">
            <SwitchField
              isMobile={isMobile}
              label="Enable pulse"
              hint="Blobs scale when scrolling fast"
              value={cfg.blobPulseEnabled}
              onChange={v => set('blobPulseEnabled', v)}
            />
            <SliderField
              isMobile={isMobile}
              label="Max velocity"
              hint="Clamps velocity before scaling"
              value={cfg.blobPulseMax}
              onChange={v => set('blobPulseMax', v)}
              min={0.5} max={10} step={0.5}
            />
            <SliderField
              isMobile={isMobile}
              label="Strength"
              hint="Scale per velocity unit"
              value={Math.round(cfg.blobPulseStrength * 100)}
              onChange={v => set('blobPulseStrength', v / 100)}
              min={1} max={30} step={1} unit="%"
            />
            <SliderField
              isMobile={isMobile}
              label="Ease duration"
              value={cfg.blobPulseDuration}
              onChange={v => set('blobPulseDuration', v)}
              min={0.05} max={2} step={0.05} unit="s"
            />
          </Section>
        </>
      )}

      {/* ════════════════════════════════════════
          ANIMATION TAB
      ════════════════════════════════════════ */}
      {tab === 'Animation' && (
        <>
          <Section label="Video reveal (scroll)" icon="◎" color="#ff6b6b">
            <div style={{
              marginBottom: 14, padding: '10px 14px',
              background: '#0a0a0a', borderRadius: 8, border: '1px solid #1a1a1a',
            }}>
              <Text style={{ fontSize: 11, color: '#444', fontFamily: 'Space Mono, monospace', lineHeight: 1.8, display: 'block' }}>
                As user scrolls, the video panel{' '}
                <span style={{ color: '#ff6b6b' }}>scales up</span> from a circle to full-screen.
                The section is <span style={{ color: '#ff6b6b' }}>pinned</span> for the duration.
              </Text>
            </div>

            <SliderField
              isMobile={isMobile}
              label="Pin length"
              hint="+=N% scroll distance while pinned"
              value={cfg.revealPinLength}
              onChange={v => set('revealPinLength', v)}
              min={50} max={500} step={10} unit="vh%"
            />

            <SliderField
              isMobile={isMobile}
              label="Scrub speed"
              hint="Higher = lags more behind scroll"
              value={cfg.revealScrub}
              onChange={v => set('revealScrub', v)}
              min={0.1} max={5} step={0.1} unit="s"
            />

            <SliderField
              isMobile={isMobile}
              label="Tween duration"
              hint="Within the scrubbed timeline"
              value={cfg.revealDuration}
              onChange={v => set('revealDuration', v)}
              min={0.1} max={1} step={0.05}
            />

            <EaseField
              isMobile={isMobile}
              label="Ease"
              hint="GSAP ease for the scale+borderRadius tween"
              value={cfg.revealEase}
              onChange={v => set('revealEase', v)}
            />
          </Section>

          <Section label="Scale & shape" icon="⊙" color="#ff6b6b">
            <SliderField
              isMobile={isMobile}
              label="Scale — start"
              hint="0 = invisible dot"
              value={cfg.revealScaleStart}
              onChange={v => set('revealScaleStart', v)}
              min={0} max={1} step={0.05}
            />
            <SliderField
              isMobile={isMobile}
              label="Scale — end"
              value={cfg.revealScaleEnd}
              onChange={v => set('revealScaleEnd', v)}
              min={0.1} max={2} step={0.05}
            />
            <SliderField
              isMobile={isMobile}
              label="Radius — start"
              hint="50 = full circle"
              value={cfg.revealBorderRadiusStart}
              onChange={v => set('revealBorderRadiusStart', v)}
              min={0} max={50} step={1} unit="%"
            />
            <SliderField
              isMobile={isMobile}
              label="Radius — end"
              hint="0 = sharp rectangle"
              value={cfg.revealBorderRadiusEnd}
              onChange={v => set('revealBorderRadiusEnd', v)}
              min={0} max={50} step={1} unit="%"
            />
          </Section>

          {/* Shape preview */}
          <div style={{
            background: '#000', border: '1px solid #222', borderRadius: 10,
            padding: 16, marginBottom: 16,
          }}>
            <Text style={{
              fontFamily: 'Space Mono, monospace', fontSize: 10, color: '#555',
              letterSpacing: '0.18em', textTransform: 'uppercase', display: 'block', marginBottom: 12,
            }}>
              Shape preview
            </Text>
            <div style={{ display: 'flex', gap: isMobile ? 12 : 20, alignItems: 'center', justifyContent: 'center' }}>
              {[
                { scale: cfg.revealScaleStart, radius: cfg.revealBorderRadiusStart, label: 'Start' },
                null,
                { scale: cfg.revealScaleEnd, radius: cfg.revealBorderRadiusEnd, label: 'End' },
              ].map((item, i) =>
                item === null ? (
                  <Text key={i} style={{ color: '#333', fontSize: 18 }}>→</Text>
                ) : (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{
                      width: Math.max(4, Math.round(80 * item.scale)),
                      height: Math.max(4, Math.round(80 * item.scale)),
                      borderRadius: `${item.radius}%`,
                      background: '#ff6b6b33',
                      border: '1px solid #ff6b6b',
                      margin: '0 auto 6px',
                      transition: 'all 0.3s',
                    }} />
                    <Text style={{ fontSize: 9, color: '#444', fontFamily: 'Space Mono, monospace' }}>{item.label}</Text>
                  </div>
                )
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Sticky save bar (fixed to bottom on mobile, sticky on desktop) ── */}
      {dirty && (
        <div style={{
          position: isMobile ? 'fixed' : 'sticky',
          bottom: isMobile ? 0 : 16,
          left: isMobile ? 0 : undefined,
          right: isMobile ? 0 : undefined,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 10,
          padding: isMobile ? '10px 14px' : '12px 16px',
          background: '#0d0d0d',
          border: '1px solid #c8f04a33',
          borderBottom: isMobile ? 'none' : undefined,
          borderLeft: isMobile ? 'none' : undefined,
          borderRight: isMobile ? 'none' : undefined,
          borderRadius: isMobile ? 0 : 12,
          marginTop: isMobile ? 0 : 8,
          zIndex: 100,
          backdropFilter: 'blur(8px)',
        }}>
          <Text style={{
            flex: 1,
            fontFamily: 'Space Mono, monospace',
            fontSize: isMobile ? 10 : 11,
            color: '#c8f04a',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            ● Unsaved changes
          </Text>
          <Button
            size={isMobile ? 'small' : 'middle'}
            onClick={handleReset}
            style={{ background: '#111', border: '1px solid #333', color: '#aaa', flexShrink: 0 }}
          >
            Discard
          </Button>
          <Button
            type="primary"
            size={isMobile ? 'small' : 'middle'}
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            style={{ background: '#c8f04a', border: 'none', color: '#000', fontWeight: 700, flexShrink: 0 }}
          >
            {isMobile ? 'Save' : 'Save to Firestore'}
          </Button>
        </div>
      )}
    </div>
  );
}